import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import liffManager from '../../../lib/liff';
import { UserEvents } from '../../../lib/analytics';
import apiClient from '../../../lib/api';
import { communicationSecretConsentStatusKey } from '../../../hooks/api';
import {
  ErrorMessage,
  LoadingSpinner,
  PageContainer,
  ContentCard,
  PageHeading,
  PrimaryButton,
} from '../../../components';
import { saveOnboardingData, confirmAndLinkFamily } from '../../../lib/onboardingSession';
import { AlreadyRegistered } from './AlreadyRegistered';
import { LinkExpired } from './LinkExpired';
import { NotYetAvailable } from './NotYetAvailable';

interface NormalizedLinkError {
  errorCode?: string;
  statusCode?: number;
  /** message / error / details.error を結合した小文字テキスト */
  text: string;
}

/**
 * API エラーを判定しやすい形に正規化する。
 * バックエンド (services/api/handlers/link.py) は機械可読コード
 * `error_code`（`TOKEN_ALREADY_USED` / `TOKEN_EXPIRED`）を付与する。
 */
const normalizeLinkError = (err: unknown): NormalizedLinkError | null => {
  const e = err as
    | {
        statusCode?: number;
        status?: number;
        message?: string;
        error?: string;
        details?: { error?: string; error_code?: string };
      }
    | null
    | undefined;
  if (!e) return null;
  return {
    errorCode: e.details?.error_code,
    statusCode: e.statusCode ?? e.status,
    text: `${e.message ?? ''} ${e.error ?? ''} ${e.details?.error ?? ''}`.toLowerCase(),
  };
};

/**
 * 招待リンクが「すでに使用済み（登録・処理済み）」エラーかを判定する。
 * 機械可読コード `TOKEN_ALREADY_USED` を最優先（文言変更に強い）。
 * フォールバック: 旧バックエンド/移行期間中はコードが無く、使用済み・
 * 期限切れともに HTTP 400 のためメッセージ文字列で切り分ける。
 */
const isLinkAlreadyUsedError = (err: unknown): boolean => {
  const e = normalizeLinkError(err);
  if (!e) return false;
  if (e.errorCode === 'TOKEN_ALREADY_USED') return true;
  if (e.errorCode === 'TOKEN_EXPIRED') return false;
  if (e.statusCode !== 400) return false;
  return e.text.includes('already been used');
};

/**
 * 招待リンクの「有効期限切れ」エラーかを判定する。
 * 機械可読コード `TOKEN_EXPIRED` を最優先。
 *
 * `TOKEN_NOT_FOUND`（HTTP 404）も期限切れ扱いとする。link_tokens テーブルは
 * TTL(expires_at) でレコードを自動削除するため、期限切れトークンは物理削除されて
 * バックエンドが「見つからない」を返す。この場合 used/expired を区別できないため、
 * 一律で期限切れ画面へ誘導する。`error_code` を持たない旧レスポンスにも備え、
 * HTTP 404 はステータスコードでも期限切れ扱いとする。
 *
 * フォールバック: HTTP 400 かつ "expired" を含み、使用済み
 * ("already been used") ではないものを期限切れとみなす。
 */
const isLinkExpiredError = (err: unknown): boolean => {
  const e = normalizeLinkError(err);
  if (!e) return false;
  if (e.errorCode === 'TOKEN_EXPIRED') return true;
  if (e.errorCode === 'TOKEN_ALREADY_USED') return false;
  if (e.errorCode === 'TOKEN_NOT_FOUND') return true;
  if (e.statusCode === 404) return true;
  if (e.statusCode !== 400) return false;
  return e.text.includes('expired') && !e.text.includes('already been used');
};

/**
 * API エラーから利用者向け表示メッセージを取り出す。
 * apiClient は Axios interceptor で ApiError(plain object) を throw するため
 * `err instanceof Error` は当てにならない。`message` / `error` /
 * `details.error` を順に参照し、無ければ fallback を返す。
 */
const getLinkErrorMessage = (err: unknown, fallback: string): string => {
  const e = err as
    | { message?: string; error?: string; details?: { error?: string } }
    | null
    | undefined;
  return e?.message || e?.error || e?.details?.error || fallback;
};

interface PatientInfo {
  patient_id: string;
  patient_name: string;
  office_id: string;
  office_name: string;
  company_name?: string;
  /**
   * 新モデル third_party_consents 由来。false なら NotYetAvailable 画面へ。
   * BE (link.py) は fail-open で常に boolean を返す設計（PR-2 / #438）のため
   * 必須型として扱い、undefined 分岐は持たない（PR-3 レビュー指摘）。
   */
  has_third_party_consent: boolean;
}


// Token validation regex - UUID v4 format (with or without hyphens)
// UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
// - 4th group starts with 4 (version 4)
// - 5th group starts with 8, 9, a, or b (variant bits)
const UUID_V4_REGEX = /^[0-9a-f]{8}-?[0-9a-f]{4}-?4[0-9a-f]{3}-?[89ab][0-9a-f]{3}-?[0-9a-f]{12}$/i;

const sanitizeToken = (token: string): string | null => {
  // Remove any potentially dangerous characters except alphanumeric and hyphens
  const cleaned = token.trim().replace(/[^a-zA-Z0-9-]/g, '');

  // Validate UUID v4 format
  if (!UUID_V4_REGEX.test(cleaned)) {
    return null;
  }

  return cleaned;
};

// Figma独自値（共有テーマに同等値が無いものはここで定義）
const PATIENT_INFO_BG = 'rgba(245, 248, 249, 0.8)';

const PatientInfoCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.sm};
  gap: ${({ theme }) => theme.spacing.xs};
  background: ${PATIENT_INFO_BG};
`;

const PatientInfoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const PatientInfoLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 70px;
`;

const PatientNameValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.xxl};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PatientInfoValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Question = styled.p`
  margin: 0;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RoleGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const RoleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const RoleGroupLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RoleRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.md};
`;

const RoleOption = styled.label<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  box-sizing: border-box;
  height: 55px;
  padding: ${({ theme }) => theme.spacing.ms};
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.backgroundTableGood : theme.colors.background};
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.primary : theme.colors.border.option};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
`;

const RoleInput = styled.input.attrs({ type: 'radio' })`
  margin: 0;
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const RoleOptionText = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 130%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// 続柄グループ（画面表示の構造）
const RELATIONSHIP_GROUPS: Array<{
  label: string;
  options: string[];
}> = [
  { label: '本人', options: ['本人'] },
  { label: '配偶者', options: ['夫', '妻'] },
  { label: '子供', options: ['息子', '娘'] },
  { label: 'その他（孫、兄弟姉妹などを含む）', options: ['その他'] },
];

export const LinkToken: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [linkToken, setLinkToken] = useState<string>('');
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  // 初期値を true にして、マウント直後の useEffect で fetch が走るまでの 1 フレームに
  // 「空のフォームカード」がフラッシュ表示されるのを防ぐ。トークン未指定/形式不正の場合のみ
  // 下の useEffect で false に倒す。
  const [isFetchingInfo, setIsFetchingInfo] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  // 招待リンクが登録・処理済みだった場合のフラグ
  const [linkAlreadyUsed, setLinkAlreadyUsed] = useState(false);
  // 招待リンクの有効期限が切れていた場合のフラグ
  const [linkExpired, setLinkExpired] = useState(false);
  // 事業所側の第三者提供同意が未取得だった場合のフラグ（設計書 §4.1 C2→C3）
  const [notYetAvailable, setNotYetAvailable] = useState(false);
  // 続柄選択
  const [relationship, setRelationship] = useState<string>('');

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // URLパラメータからトークンを取得して患者情報を取得
  useEffect(() => {
    isMountedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const rawToken = params.get('invitationToken');

    if (rawToken) {
      const sanitized = sanitizeToken(rawToken);
      if (sanitized) {
        setLinkToken(sanitized);
        fetchPatientInfo(sanitized);
      } else {
        setError('無効な招待コード形式です');
        setIsFetchingInfo(false);
      }
    } else {
      // トークン未指定: ローディングを解除してフォーム側のエラー表示に委ねる
      setIsFetchingInfo(false);
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false;

      // Abort any pending fetch requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchPatientInfo = async (token: string) => {
    setIsFetchingInfo(true);
    setError('');

    try {
      // Use centralized API client
      const data = await apiClient.verifyLinkToken(token);

      // Check if component is still mounted
      if (!isMountedRef.current) return;

      // 事業所側で第三者提供同意（新モデル third_party_consents）が未取得なら、
      // 続柄選択に進ませず「まだご利用いただけません」画面へ流入させる（設計書 §4.1 C2→C3）。
      // 後方互換フィールド has_current_consent はフェーズ 3 で削除予定。
      //
      // fail-closed: `true` 以外（false / undefined / null）を全てブロックする。
      // CommunicationConsentGate 側 (`data.has_current !== true`) と方針を揃え、
      // BE の version skew 等でフィールドが欠落した場合も安全側に倒す（法的同意ゲート
      // としての多重防御 / PR-3 レビュー指摘）。
      if (data.has_third_party_consent !== true) {
        setNotYetAvailable(true);
        return;
      }

      setPatientInfo({
        patient_id: String(data.patient_id),
        patient_name: data.patient_name || '利用者様',
        office_id: data.office_id || '',
        office_name: data.office_name || '事業所',
        company_name: data.company_name || '',
        // BE は has_third_party_consent を fail-open で必須の boolean として返すため
        // ?? false は不要（apiClient.verifyLinkToken の戻り値型が必須）。
        // 旧 consents 由来の has_current_consent は verifyLinkToken の戻り値には残るが、
        // LIFF FE では読み取り経路が存在しないため PatientInfo に持ち込まない
        // （PR-3 レビュー nit 指摘 / デッドフィールド排除）。
        has_third_party_consent: data.has_third_party_consent,
      });
    } catch (err) {
      // Only set error if component is still mounted
      if (isMountedRef.current) {
        if (isLinkAlreadyUsedError(err)) {
          setLinkAlreadyUsed(true);
        } else if (isLinkExpiredError(err)) {
          setLinkExpired(true);
        } else {
          setError(getLinkErrorMessage(err, '情報の取得に失敗しました'));
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetchingInfo(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!linkToken.trim()) {
      setError('招待コードを入力してください');
      return;
    }
    if (!relationship) {
      setError('続柄を選択してください');
      return;
    }
    if (!patientInfo) {
      setError('患者情報が取得できていません');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // LINE ユーザーID取得
      const lineUserId = await liffManager.getLineUserId();
      if (!lineUserId) {
        throw new Error('LINEユーザーIDを取得できませんでした');
      }

      const patientId = parseInt(patientInfo.patient_id, 10);
      const isSelf = relationship === '本人';

      // オンボーディングデータをsessionStorageに保存
      // hasCurrentConsent は廃止（旧 consents 由来のフラグ・第三者提供同意は事業所側が取得する）。
      // 通信の秘密同意の取得状況は GET /api/consent/communication-secret/status で判定する。
      saveOnboardingData({
        token: linkToken,
        patientId,
        patientName: patientInfo.patient_name,
        officeId: patientInfo.office_id,
        officeName: patientInfo.office_name,
        companyName: patientInfo.company_name || '',
        relationship,
        lineUserId,
      });

      // Analytics: リンクトークン使用イベント
      UserEvents.linkTokenUsed();

      // 通信の秘密同意（LINE アカウント単位）の取得状況を確認して遷移先を決める。
      // has_current === true → 既に取得済 → 連携処理へ直行
      // has_current === false → 未取得 or version 不一致 → /consent へ誘導
      // 判定失敗（ネットワーク・BE 5xx 等）は「未取得」と決め打ちしない:
      // 既に同意済みの既存家族が一時障害で /consent onboarding フローに送られると、
      // 追記専用テーブル (communication_secret_consents) に重複レコードが作成される
      // リスクがある。CommunicationConsentGate の fail-safe 方針（一時障害は
      // ErrorMessage + retry で止める）と揃え、ここもエラー表示 + リトライにする
      // （PR-3 レビュー warning 指摘 / fail-closed 方針矛盾の解消）。
      let hasCommunicationSecretConsent: boolean;
      try {
        const status = await apiClient.getCommunicationSecretConsentStatus();
        // react-query キャッシュを温めておく: 遷移先で CommunicationConsentGate が
        // useCommunicationSecretConsentStatus() を再度発火した際に、同じデータを
        // 再フェッチせずキャッシュから読ませる（PR-3 レビュー nit 指摘 / パフォーマンス）。
        // ConsentPage.handleComplete でも同型の setQueryData を行っており一貫させる。
        queryClient.setQueryData(communicationSecretConsentStatusKey, status);
        hasCommunicationSecretConsent = status.has_current;
      } catch (statusError) {
        console.warn('通信の秘密同意ステータス取得に失敗しました:', statusError);
        setIsSubmitting(false);
        setError(
          '通信の秘密同意の取得状況を確認できませんでした。しばらく待ってから再度お試しください。',
        );
        return;
      }

      if (!hasCommunicationSecretConsent) {
        // 未取得 → /consent へ（onboarding フロー）。
        // 家族連携 (confirmAndLinkFamily) は ConsentPage.handleComplete が
        // 担当するため、state.returnTo は積まない（`isGate` フラグも積まない）。
        // Gate 経路と混同されると confirmAndLinkFamily が呼ばれず家族連携未完了に
        // なるため（PR-3 レビュー第 4 巡 critical 指摘）、明示的に onboarding 経路
        // であることを示すよう Gate 用フィールドは一切載せない。
        navigate('/consent', {
          state: {
            relationship,
            patientId,
            patientName: patientInfo.patient_name,
            companyName: patientInfo.company_name,
          },
        });
        return;
      }

      // 取得済 → 続柄別に連携処理
      if (isSelf) {
        // 本人 + 同意済み → 家族連携してホームへ。LINE 表示名で自動プロフィール作成。
        const linkedPatientId = await confirmAndLinkFamily(queryClient);
        try {
          let profileDisplayName = patientInfo.patient_name;
          try {
            const lineProfile = await liffManager.getProfile();
            if (lineProfile?.displayName) {
              profileDisplayName = lineProfile.displayName;
            }
          } catch (profileFetchError) {
            console.warn('LINE表示名の取得に失敗しました。利用者名を使用します:', profileFetchError);
          }
          await apiClient.saveUserProfile({ display_name: profileDisplayName });
          queryClient.setQueryData(['userProfile', 'check'], { completed: true });
          // 2人目以降の連携時にホーム画面が既存利用者に着地しないよう、
          // 新規連携した patient_id をクエリに乗せて遷移する。
          navigate(`/?patient_id=${linkedPatientId}`, { replace: true });
        } catch (profileError) {
          // 家族連携は完了済みのため、プロフィール画面へフォールバック
          console.error('プロフィール自動作成に失敗しました:', profileError);
          navigate('/menu/profile', { replace: true });
        }
      } else {
        // 本人以外 + 同意済み → プロフィール登録済みなら連携直行、未登録ならプロフィール画面へ。
        let profileCompleted = false;
        try {
          const profileCheck = await apiClient.checkUserProfile();
          profileCompleted = profileCheck.completed;
        } catch (profileCheckError) {
          console.warn('プロフィール完了状態の確認に失敗しました:', profileCheckError);
        }

        if (profileCompleted) {
          try {
            const linkedPatientId = await confirmAndLinkFamily(queryClient);
            queryClient.setQueryData(['userProfile', 'check'], { completed: true });
            navigate(`/?patient_id=${linkedPatientId}`, { replace: true });
          } catch (linkError) {
            console.error('家族連携に失敗しました:', linkError);
            navigate('/menu/profile', { replace: true });
          }
        } else {
          navigate('/menu/profile', { replace: true });
        }
      }
      // 成功時はいずれかの navigate(...) で遷移するため、
      // ここで setIsSubmitting(false) を呼ぶと遷移直前に招待コード入力画面が一瞬再描画される。
      // isSubmitting は true のまま維持し、遷移完了に任せる。
    } catch (err) {
      // 失敗時は画面遷移しないため、ローディング表示を解除する。
      // LinkAlreadyUsed / LinkExpired は別コンポーネントを返すため
      // isSubmitting の値はその描画に影響しないが、状態として整合させておく。
      setIsSubmitting(false);
      if (isLinkAlreadyUsedError(err)) {
        setLinkAlreadyUsed(true);
        return;
      }
      if (isLinkExpiredError(err)) {
        setLinkExpired(true);
        return;
      }
      setError(getLinkErrorMessage(err, '連携処理に失敗しました'));
    }
  };

  const patientName = patientInfo?.patient_name ?? '';
  const questionText = patientName
    ? `${patientName}さまから見た関係を選択してください。`
    : '続柄を選択してください。';

  if (linkAlreadyUsed) {
    return <AlreadyRegistered />;
  }

  if (linkExpired) {
    return <LinkExpired />;
  }

  if (notYetAvailable) {
    return <NotYetAvailable />;
  }

  if (isSubmitting) {
    return <LoadingSpinner fullPage message="処理中..." />;
  }

  // 認証確認中（LinkTokenPage）→ 情報取得中 → 処理中 を同一の LoadingSpinner DOM で
  // 連続させ、ローディング画面が点滅しないようにする。
  if (isFetchingInfo) {
    return <LoadingSpinner fullPage message="情報を取得中..." />;
  }

  return (
    <PageContainer>
      <PageHeading>はじめにアカウントを設定してください。</PageHeading>

      <ContentCard>
        {patientInfo && (
          <PatientInfoCard>
            <PatientInfoRow>
              <PatientInfoLabel>利用者名：</PatientInfoLabel>
              <PatientNameValue>{patientInfo.patient_name}さま</PatientNameValue>
            </PatientInfoRow>
            <PatientInfoRow>
              <PatientInfoLabel>事業所名：</PatientInfoLabel>
              <PatientInfoValue>{patientInfo.office_name}</PatientInfoValue>
            </PatientInfoRow>
          </PatientInfoCard>
        )}

        <Question>{questionText}</Question>

        {error && <ErrorMessage title="エラー" description={error} />}

        <RoleGroupContainer>
          {RELATIONSHIP_GROUPS.map((group) => (
            <RoleGroup key={group.label}>
              <RoleGroupLabel>{group.label}</RoleGroupLabel>
              <RoleRow>
                {group.options.map((option) => (
                  <RoleOption key={option} $selected={relationship === option}>
                    <RoleInput
                      name="relationship"
                      value={option}
                      checked={relationship === option}
                      onChange={(e) => setRelationship(e.target.value)}
                    />
                    <RoleOptionText>{option}</RoleOptionText>
                  </RoleOption>
                ))}
              </RoleRow>
            </RoleGroup>
          ))}
        </RoleGroupContainer>

        <PrimaryButton
          onClick={handleSubmit}
          disabled={!patientInfo || !relationship || isSubmitting}
        >
          次へ
        </PrimaryButton>
      </ContentCard>
    </PageContainer>
  );
};

export default LinkToken;